import { Button, Tooltip } from "antd";
import { InternalControlSearchProps, TSearchValue, TSearchValueDisabled } from "./search-template";
import { getMemberColor } from "@src/util/intelligence-search/get-member-info";
import { FILTER_OPERATORS } from "@src/pages/intelligence-search/constant/filter-operator";
import { CloseOutlined } from "@ant-design/icons";
import { SmlAsset } from "@src/types/intelligence-search";
import { PERMISSION_LAYOUT_CHECK } from "../crud-permission-layout";

type TagInfo = {
    disabled?: TSearchValueDisabled;
    showClear?: boolean;
    isCustomMetric?: boolean;
    meta: SmlAsset;
    name: string;
    value?: any;
    operator?: string;
};
function renderLabelTag(meta: Record<string, any>, operator?: string, value?: any): React.ReactNode {
    const name = meta.name;

    const operatorLabel = FILTER_OPERATORS.find((op) => op.value === operator)?.label || operator;

    const labelName = meta.title ? meta.title : name;
    if (!operatorLabel) {
        return labelName;
    }
    const formatValues = (vals: string[] | number[] | string | number | boolean) => {
        if (typeof vals === "boolean") {
            return String(vals);
        }
        if (typeof vals === "string" || typeof vals === "number") {
            return vals;
        }
        if (Array.isArray(vals)) {
            if (vals.length > 3) {
                return `${vals.slice(0, 2).join(", ")}, +${vals.length - 2} more`;
            }
            return vals.join(", ");
        }
        return vals;
    };

    return (
        <div className="flex space-x-2">
            <span>{labelName}</span>
            <span>{operatorLabel.toLowerCase()}</span>
            <span>{formatValues(value ?? [])}</span>
        </div>
    );
}

export const SearchRenderTags: React.FC<{ values: TSearchValue[]; control: InternalControlSearchProps }> = ({
    values,
    control,
}) => {
    return (
        <>
            {values.map((value) => {
                const tagInfos: TagInfo[] = [];

                const generalInfo: TagInfo = {
                    showClear: value.showClear ?? true,
                    disabled: value.disabled,
                    isCustomMetric: value.isCustomMetric,
                    meta: value.meta,
                    name: value.name || value.meta.name,
                };
                if (value.name) {
                    tagInfos.push(generalInfo);
                }
                if (value.filter) {
                    tagInfos.push({
                        ...generalInfo,
                        showClear: true,
                        value: value.filter.value,
                        operator: value.filter.operator,
                    });
                }
                return (
                    <div key={value.name} className="flex flex-wrap gap-2">
                        {tagInfos.map((tagInfo) => (
                            <SearchTag
                                key={tagInfo.name + String(tagInfo.operator)}
                                oriValue={value}
                                tagInfo={tagInfo}
                                control={control}
                            />
                        ))}
                    </div>
                );
            })}
        </>
    );
};

const SearchTag: React.FC<{
    oriValue: TSearchValue;
    tagInfo: TagInfo;
    control: InternalControlSearchProps;
}> = ({ oriValue, tagInfo, control }) => {
    const { meta, name, value, operator, isCustomMetric, disabled, showClear } = tagInfo;
    const isFilter = !!operator;
    const isDisabled = disabled?.status && !isFilter;

    const memberType = operator ? "filter" : meta.asset_type;
    const { backgroundColorSecondary } = getMemberColor(memberType);

    return (
        <div
            className={`${PERMISSION_LAYOUT_CHECK} can-edit flex justify-start items-center  shadow-lg rounded-[10px] overflow-hidden `}
            onClick={() => {
                if (isDisabled) return;
                if (isCustomMetric) {
                    const meta = oriValue.meta;
                    control.handleOpenCustomMetricModal?.(meta, meta.id, meta.name);
                } else {
                    control.handleOpenFilterModal?.(name, meta);
                }
            }}
        >
            <Tooltip
                title={
                    isDisabled
                        ? disabled?.reason
                        : isCustomMetric
                        ? "Metric is created by user, click to edit"
                        : isFilter
                        ? "Click to edit filter"
                        : "Click to add filter"
                }
            >
                <div className={`flex cursor-pointer `}>
                    <div
                        style={
                            isDisabled
                                ? {
                                      color: getMemberColor().textColor,
                                      backgroundColor: getMemberColor().backgroundColorSecondary,
                                  }
                                : {
                                      backgroundColor: backgroundColorSecondary,
                                  }
                        }
                        className={`px-2 flex items-center`}
                    >
                        {renderLabelTag(meta, operator, value)}
                        <Tooltip title={showClear ? "" : "This dimension is used in other questions"}>
                            <Button
                                type="text"
                                disabled={isDisabled || !showClear}
                                className="ml-2"
                                onClick={(e) => {
                                    if (isDisabled || !showClear) return;
                                    e.stopPropagation();
                                    control.handleRemove?.(oriValue, isFilter ? "filter" : "normal");
                                }}
                                icon={<CloseOutlined />}
                            />
                        </Tooltip>
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};
